import { RouteResourceUpdater } from 'react-resource-router';

import { urls as mergeCheckUrls } from 'src/components/settings/merge-checks/urls';
import repoUrls from 'src/sections/repository/urls';
import {
  BranchConfig,
  CustomMergeCheck,
  CustomMergeCheckConfigRouteResource,
  CustomMergeCheckExtensionId,
  CustomMergeCheckKey,
  CustomMergeCheckResourceId,
  CustomMergeCheckSettingsRouterParams,
  CustomMergeChecksResourceType,
} from 'src/types/custom-merge-checks';
import projectUrls from 'src/urls/projects';

/**
 * Generate a CustomMergeCheckKey that uniquely identifies a custom merge check
 * for a given resource type, branch config and extension ID. These are used to
 * maintain a local map of the configuration state ('off', 'required' or
 * 'recommended') of custom merge checks.
 * @param resourceType the CustomMergeChecksResourceType of the check
 * @param branchConfig the branch config of the check
 * @param extensionId the Forge extension ID of the check
 * @returns a CustomMergeCheckKey identifying a check
 */
export const customMergeCheckKey = (
  resourceType: CustomMergeChecksResourceType,
  branchConfig: BranchConfig,
  extensionId: CustomMergeCheckExtensionId
): CustomMergeCheckKey =>
  `${resourceType}${branchConfig.type}${branchConfig.identifier}${extensionId}`;

export const REPOSITORY_ARI_PREFIX = 'ari:cloud:bitbucket::repository';
export const PROJECT_ARI_PREFIX = 'ari:cloud:bitbucket::project';
export const WORKSPACE_ARI_PREFIX = 'ari:cloud:bitbucket::workspace';

export const determineResourceType = (
  resource: CustomMergeCheckResourceId
): CustomMergeChecksResourceType => {
  if (resource.startsWith(REPOSITORY_ARI_PREFIX)) {
    return CustomMergeChecksResourceType.Repository;
  }

  if (resource.startsWith(PROJECT_ARI_PREFIX)) {
    return CustomMergeChecksResourceType.Project;
  }

  if (resource.startsWith(WORKSPACE_ARI_PREFIX)) {
    return CustomMergeChecksResourceType.Workspace;
  }

  throw new Error(`Invalid resource ID ${resource}`);
};

export const updateCustomMergeCheckConfigRouteResource =
  ({
    extensionId,
    branchConfig,
    state,
    resource,
  }: CustomMergeCheck): RouteResourceUpdater<CustomMergeCheckConfigRouteResource> =>
  data => {
    const updatedStateMap = new Map(data.stateMap);
    const key = customMergeCheckKey(
      determineResourceType(resource),
      branchConfig,
      extensionId
    );
    updatedStateMap.set(key, state);
    return {
      ...data,
      stateMap: updatedStateMap,
    };
  };

export const updateCheckConfigResourceWithCheck =
  (
    updatedCheck: CustomMergeCheck
  ): RouteResourceUpdater<CustomMergeCheckConfigRouteResource> =>
  data => {
    const { enabledChecks } = data;

    const idx = enabledChecks.findIndex(
      check => check.uuid === updatedCheck.uuid
    );
    if (idx >= 0) {
      enabledChecks[idx] = updatedCheck;
    } else {
      enabledChecks.push(updatedCheck);
    }

    return {
      ...data,
      enabledChecks: [...enabledChecks],
    };
  };

export const removeCheckFromCheckConfigResource =
  (uuid: string): RouteResourceUpdater<CustomMergeCheckConfigRouteResource> =>
  data => {
    const { enabledChecks } = data;

    return {
      ...data,
      enabledChecks: enabledChecks.filter(check => check.uuid !== uuid),
    };
  };

export const RESOURCE_TYPE_SORT_COMPARATOR = (
  a: CustomMergeChecksResourceType,
  b: CustomMergeChecksResourceType
): number => {
  if (a === b) {
    return 0;
  }

  if (
    a === CustomMergeChecksResourceType.Workspace ||
    b === CustomMergeChecksResourceType.Repository
  ) {
    return -1;
  }

  return 1;
};

export const CUSTOM_MERGE_CHECK_SORT_COMPARATOR = (
  a: CustomMergeCheck,
  b: CustomMergeCheck
): number => {
  return RESOURCE_TYPE_SORT_COMPARATOR(
    determineResourceType(a.resource),
    determineResourceType(b.resource)
  );
};

export const getBranchRestrictionsUrl = (
  resourceType: CustomMergeChecksResourceType,
  routerParams: CustomMergeCheckSettingsRouterParams
): string | undefined => {
  if (resourceType === CustomMergeChecksResourceType.Workspace) {
    return mergeCheckUrls.branchRestrictions;
  }

  const { repositoryOwner, repositorySlug, workspaceSlug, projectKey } =
    routerParams;

  if (
    resourceType === CustomMergeChecksResourceType.Project &&
    workspaceSlug &&
    projectKey
  ) {
    return projectUrls.ui.branchRestrictions(workspaceSlug, projectKey);
  }

  // Repository pages use legacy paths, so we need to use repositoryOwner instead of workspaceSlug
  if (
    resourceType === CustomMergeChecksResourceType.Repository &&
    repositoryOwner &&
    repositorySlug
  ) {
    return repoUrls.ui.adminPage(
      repositoryOwner,
      repositorySlug,
      'branch-restrictions'
    );
  }

  return undefined;
};
